import React, { useState, useCallback } from 'react';
import { Home, Camera, AlertTriangle, FileText, Plus, Trash2, AlertCircle } from 'lucide-react';
import sendVesselFailureReportEmail from './vesselFailureReportEmailService';
import { validateFormCompletion, markVesselScrap } from './formTraceabilityService';
import SerialValidationModal from './SerialValidationModal';
import PVSPageLayout from './PVSPageLayout';
import FormSuccessModal from './FormSuccessModal';

const VesselFailureReportForm = ({ onBack }) => {
  const [formData, setFormData] = useState({
    dateLogged: new Date().toISOString().slice(0, 10),
    loggedBy: '',
    departmentLocation: '',
    status: 'Draft',
    vesselSerialNumber: '',
    manufacturer: '',
    modelVesselType: '',
    vesselWaterCapacity: '',
    yearOfManufacture: '',
    failureCategories: [],
    reasonForFailure: '',
    operationalState: '',
    immediateRiskLevel: '',
    anyInjuries: 'no',
    injuryDetails: '',
    impactOnOps: '',
    impactDetails: '',
    attachments: [],
    inspectionReadings: [],
    witnessStatements: [],
    suspectedRootCause: '',
    rootCauseDetails: '',
    canFailureBeRectified: 'yes',
    vesselToBeScrapped: 'no',
    correctiveActionVerified: 'no',
    updatedInVesselInventory: false,
    qcManagerSignOff: '',
    qcManagerSignOffDate: '',
    dateClosed: ''
  });

  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [serialModalOpen, setSerialModalOpen] = useState(false);
  const [serialModalMessage, setSerialModalMessage] = useState('');
  const closeSerialModal = useCallback(() => setSerialModalOpen(false), []);

  const failureCategoryOptions = ['Structural', 'Corrosion', 'Paintwork', 'Weld', 'Other'];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCategoryToggle = (category) => {
    setFormData(prev => ({
      ...prev,
      failureCategories: prev.failureCategories.includes(category)
        ? prev.failureCategories.filter(c => c !== category)
        : [...prev.failureCategories, category]
    }));
  };

  const addInspectionReading = () => {
    setFormData(prev => ({ ...prev, inspectionReadings: [...prev.inspectionReadings, { id: Date.now(), date: new Date().toISOString().slice(0, 10), sensor: '', value: '', units: '', operator: '' }] }));
  };

  const updateInspectionReading = (id, field, value) => {
    setFormData(prev => ({ ...prev, inspectionReadings: prev.inspectionReadings.map(r => r.id === id ? { ...r, [field]: value } : r) }));
  };

  const removeInspectionReading = (id) => {
    setFormData(prev => ({ ...prev, inspectionReadings: prev.inspectionReadings.filter(r => r.id !== id) }));
  };

  const addWitnessStatement = () => {
    setFormData(prev => ({ ...prev, witnessStatements: [...prev.witnessStatements, { id: Date.now(), name: '', role: '', statement: '', date: new Date().toISOString().slice(0, 10) }] }));
  };

  const updateWitnessStatement = (id, field, value) => {
    setFormData(prev => ({ ...prev, witnessStatements: prev.witnessStatements.map(w => w.id === id ? { ...w, [field]: value } : w) }));
  };

  const removeWitnessStatement = (id) => {
    setFormData(prev => ({ ...prev, witnessStatements: prev.witnessStatements.filter(w => w.id !== id) }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({ id: Date.now() + Math.random(), file, name: file.name, size: file.size, type: file.type, preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null }));
    setFormData(prev => ({ ...prev, attachments: [...prev.attachments, ...newAttachments] }));
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    const newAttachments = files.map(file => ({ id: Date.now() + Math.random(), file, name: file.name, size: file.size, type: file.type, preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null }));
    setFormData(prev => ({ ...prev, attachments: [...prev.attachments, ...newAttachments] }));
  };

  const removeAttachment = (id) => {
    setFormData(prev => ({ ...prev, attachments: prev.attachments.filter(a => a.id !== id) }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage('');
    const requiredFields = [
      { field: 'loggedBy', label: 'Logged By' },
      { field: 'departmentLocation', label: 'Department/Site Location' },
      { field: 'vesselSerialNumber', label: 'Vessel Serial Number' },
      { field: 'reasonForFailure', label: 'Reason for Failure' },
      { field: 'immediateRiskLevel', label: 'Immediate Risk Level' }
    ];
    const missingFields = requiredFields.filter(f => !formData[f.field] || formData[f.field].trim() === '');
    if (missingFields.length > 0) { setSubmitMessage(`Please fill in the following required fields: ${missingFields.map(f => f.label).join(', ')}`); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    if (formData.failureCategories.length === 0) { setSubmitMessage('Please select at least one Failure Category.'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    if (formData.reasonForFailure.length < 10) { setSubmitMessage('Reason for Failure must be at least 10 characters.'); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }

    setIsSubmitting(true);

    try {
      await validateFormCompletion(formData.vesselSerialNumber, 'vesselFailureReport');
    } catch (error) {
      console.error('Serial validation failed:', error);
      setSerialModalMessage(error.message || 'Vessel Serial Number not found in Vessel Inventory.');
      setSerialModalOpen(true);
      setIsSubmitting(false);
      return;
    }

    try {
      const submitData = { ...formData, status: 'Investigating', dateTimeLogged: formData.dateTimeLogged };
      await sendVesselFailureReportEmail(submitData);
      await markVesselScrap(formData.vesselSerialNumber);
      setFormData(prev => ({ ...prev, status: 'Investigating' }));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage(error.message || 'Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isHighRisk = formData.immediateRiskLevel === 'High';

  return (
    <PVSPageLayout className="py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="pvs-glass rounded-xl shadow-pvs-glow overflow-hidden border border-pvs-accent/30">
          
          {/* Persistent Header */}
          <div className="border-b border-pvs-border p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-white">Vessel Failure Report</h1>
                <p className="text-xs text-pvs-text-dim mt-1">Ref: PVS-VFR-001 | Version: 1.0</p>
              </div>
              <button type="button" onClick={onBack} className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200" title="Return to Forms">
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
            <p className="text-pvs-text-dim text-center">Nonconformance & Failure Documentation</p>
          </div>

          {/* High Risk Alert Banner */}
          {isHighRisk && (
            <div className="bg-red-600 text-white px-6 py-3 flex items-center">
              <AlertTriangle className="w-6 h-6 mr-3 animate-pulse" />
              <div>
                <p className="font-bold">IMMEDIATE RISK LEVEL: HIGH</p>
                <p className="text-sm">This report has been flagged for emergency response. Notify supervisor immediately.</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {/* Submit Message */}
            {submitMessage && (
              <div className={`p-4 rounded-lg border-2 ${
                submitMessage.includes('Error') || submitMessage.includes('Please') || submitMessage.includes('not found') || submitMessage.includes('Unable to validate')
                  ? 'bg-red-900/30 text-red-400 border-red-500/30'
                  : 'bg-green-900/30 text-pvs-accent border-green-500/30'
              }`}>
                <p className="font-medium">{submitMessage}</p>
              </div>
            )}

            {/* Section 1: Report Information */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4 flex items-center">
                <span className="w-8 h-8 rounded-full bg-pvs-accent text-pvs-bg flex items-center justify-center text-sm mr-3">1</span>
                Report Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Date Logged *</label><input type="date" name="dateLogged" value={formData.dateLogged} onChange={handleInputChange} className="block w-full pvs-input" required /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Logged By *</label><input type="text" name="loggedBy" value={formData.loggedBy} onChange={handleInputChange} placeholder="Your name" className="block w-full pvs-input" required /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Department / Site Location *</label><input type="text" name="departmentLocation" value={formData.departmentLocation} onChange={handleInputChange} placeholder="e.g., Main Workshop, Site A" className="block w-full pvs-input" required /></div>
              </div>
            </div>

            {/* Section 2: Vessel Identification */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4 flex items-center">
                <span className="w-8 h-8 rounded-full bg-pvs-accent text-pvs-bg flex items-center justify-center text-sm mr-3">2</span>
                Vessel Identification
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Vessel Serial Number *</label><input type="text" name="vesselSerialNumber" value={formData.vesselSerialNumber} onChange={handleInputChange} placeholder="Enter or scan serial number" className="block w-full pvs-input" required /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Manufacturer</label><input type="text" name="manufacturer" value={formData.manufacturer} onChange={handleInputChange} className="block w-full pvs-input" /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Model / Vessel Type</label><input type="text" name="modelVesselType" value={formData.modelVesselType} onChange={handleInputChange} className="block w-full pvs-input" /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Vessel Water Capacity (Litres)</label><input type="number" name="vesselWaterCapacity" value={formData.vesselWaterCapacity} onChange={handleInputChange} className="block w-full pvs-input" /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Year of Manufacture</label><input type="number" name="yearOfManufacture" value={formData.yearOfManufacture} onChange={handleInputChange} min="1900" max={new Date().getFullYear()} placeholder="YYYY" className="block w-full pvs-input" /></div>
              </div>
            </div>

            {/* Section 3: Failure Event Summary */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4 flex items-center">
                <span className="w-8 h-8 rounded-full bg-pvs-accent text-pvs-bg flex items-center justify-center text-sm mr-3">3</span>
                Failure Event Summary
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Failure Category * <span className="text-xs text-pvs-text-dim">(Select all that apply)</span></label>
                  <div className="flex flex-wrap gap-2">
                    {failureCategoryOptions.map(category => (
                      <button key={category} type="button" onClick={() => handleCategoryToggle(category)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${formData.failureCategories.includes(category) ? 'bg-pvs-accent text-pvs-bg' : 'bg-pvs-input-bg text-pvs-text-muted hover:bg-pvs-card-light'}`}>{category}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Reason for Failure (Initial Assessment) * <span className="text-xs text-pvs-text-dim">(Min 10 characters)</span></label>
                  <textarea name="reasonForFailure" value={formData.reasonForFailure} onChange={handleInputChange} rows={3} minLength={10} placeholder="Describe the failure in detail..." className={`block w-full rounded-md shadow-sm px-3 py-2 border ${formData.reasonForFailure && formData.reasonForFailure.length < 10 ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-pvs-input-bg text-white' : 'pvs-input'}`} required />
                  <p className="text-xs text-pvs-text-dim mt-1">{formData.reasonForFailure.length}/10 characters minimum</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Operational State at Time of Failure</label>
                  <select name="operationalState" value={formData.operationalState} onChange={handleInputChange} className="block w-full pvs-select">
                    <option value="">Select...</option>
                    <option value="In Service">In Service</option>
                    <option value="Idle">Idle</option>
                    <option value="Under Test">Under Test</option>
                    <option value="Commissioning">Commissioning</option>
                    <option value="Maintenance">Under Maintenance</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Section 4: Safety & Impact */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4 flex items-center">
                <span className="w-8 h-8 rounded-full bg-pvs-accent text-pvs-bg flex items-center justify-center text-sm mr-3">4</span>
                Safety & Impact Assessment
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Immediate Risk Level *</label>
                  <div className="flex gap-4">
                    {['Low', 'Medium', 'High'].map(level => (
                      <label key={level} className={`flex-1 p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                        formData.immediateRiskLevel === level
                          ? level === 'High' ? 'bg-red-900/30 border-red-500 text-red-400'
                            : level === 'Medium' ? 'bg-amber-900/20 border-amber-500 text-amber-400'
                              : 'bg-green-900/30 border-green-500 text-green-400'
                          : 'bg-pvs-card-light border-pvs-border hover:border-pvs-text-dim'
                      }`}>
                        <input type="radio" name="immediateRiskLevel" value={level} checked={formData.immediateRiskLevel === level} onChange={handleInputChange} className="sr-only" />
                        <span className="font-semibold">{level}</span>
                        {level === 'High' && formData.immediateRiskLevel === level && (<AlertTriangle className="w-5 h-5 mx-auto mt-1 text-red-400" />)}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Any Injuries or Near Misses?</label>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center text-pvs-text-muted"><input type="radio" name="anyInjuries" value="no" checked={formData.anyInjuries === 'no'} onChange={handleInputChange} className="mr-2" />No</label>
                    <label className="flex items-center text-pvs-text-muted"><input type="radio" name="anyInjuries" value="yes" checked={formData.anyInjuries === 'yes'} onChange={handleInputChange} className="mr-2" />Yes</label>
                  </div>
                  {formData.anyInjuries === 'yes' && (
                    <input type="text" name="injuryDetails" value={formData.injuryDetails} onChange={handleInputChange} placeholder="Provide details..." className="mt-2 block w-full rounded-md border-red-500 bg-red-900/30 shadow-sm focus:border-red-500 focus:ring-red-500 px-3 py-2 border text-red-400" />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Impact on Operations</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <select name="impactOnOps" value={formData.impactOnOps} onChange={handleInputChange} className="block w-full pvs-select">
                      <option value="">Select impact level...</option>
                      <option value="None">None - No operational impact</option>
                      <option value="Minor">Minor - Workaround available</option>
                      <option value="Moderate">Moderate - Reduced capacity</option>
                      <option value="Major">Major - Operations halted</option>
                      <option value="Critical">Critical - Safety shutdown</option>
                    </select>
                    <input type="text" name="impactDetails" value={formData.impactDetails} onChange={handleInputChange} placeholder="Additional impact details..." className="block w-full pvs-input" />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5: Evidence Package */}
            <div className="border-b border-pvs-border pb-6" id="evidenceSection">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4 flex items-center">
                <span className="w-8 h-8 rounded-full bg-pvs-accent text-pvs-bg flex items-center justify-center text-sm mr-3">5</span>
                Evidence Package
              </h2>

              <div className="mb-6">
                <label className="text-sm font-medium text-pvs-text-muted mb-3 block">Photos / Files / Attachments</label>
                <div onDragOver={handleDragOver} onDrop={handleDrop} className="border-2 border-dashed border-pvs-border rounded-lg p-6 text-center hover:border-pvs-accent transition-colors cursor-pointer">
                  <input type="file" id="fileUpload" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" onChange={handleFileUpload} className="hidden" />
                  <label htmlFor="fileUpload" className="cursor-pointer">
                    <Camera className="w-10 h-10 mx-auto text-pvs-text-dim mb-3" />
                    <p className="text-pvs-text-muted font-medium">Drop files here or click to upload</p>
                    <p className="text-pvs-text-dim text-sm mt-1">Supports images, PDF, Word, Excel files</p>
                  </label>
                </div>

                {formData.attachments.length > 0 && (
                  <div className="mt-4 space-y-3">
                    <p className="text-sm font-medium text-pvs-text-muted">{formData.attachments.length} file(s) attached:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {formData.attachments.map((attachment) => (
                        <div key={attachment.id} className="flex items-center p-3 bg-pvs-card-light rounded-lg border border-pvs-border">
                          {attachment.preview ? (
                            <img src={attachment.preview} alt={attachment.name} className="w-12 h-12 object-cover rounded mr-3" />
                          ) : (
                            <div className="w-12 h-12 bg-pvs-accent/10 rounded flex items-center justify-center mr-3">
                              <FileText className="w-6 h-6 text-pvs-accent" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{attachment.name}</p>
                            <p className="text-xs text-pvs-text-dim">{formatFileSize(attachment.size)}</p>
                          </div>
                          <button type="button" onClick={() => removeAttachment(attachment.id)} className="ml-2 text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Inspection Readings */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium text-pvs-text-muted">Inspection Readings</label>
                  <button type="button" onClick={addInspectionReading} className="flex items-center text-sm text-pvs-accent hover:text-pvs-accent/80"><Plus className="w-4 h-4 mr-1" /> Add Reading</button>
                </div>
                {formData.inspectionReadings.length === 0 ? (
                  <p className="text-pvs-text-dim text-sm italic">No inspection readings added yet.</p>
                ) : (
                  <div className="space-y-3">
                    {formData.inspectionReadings.map((reading) => (
                      <div key={reading.id} className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3 bg-pvs-card-light rounded-lg">
                        <input type="date" value={reading.date} onChange={(e) => updateInspectionReading(reading.id, 'date', e.target.value)} className="rounded pvs-input text-sm" />
                        <input type="text" value={reading.sensor} onChange={(e) => updateInspectionReading(reading.id, 'sensor', e.target.value)} placeholder="Sensor" className="rounded pvs-input text-sm" />
                        <input type="text" value={reading.value} onChange={(e) => updateInspectionReading(reading.id, 'value', e.target.value)} placeholder="Value" className="rounded pvs-input text-sm" />
                        <input type="text" value={reading.units} onChange={(e) => updateInspectionReading(reading.id, 'units', e.target.value)} placeholder="Units" className="rounded pvs-input text-sm" />
                        <div className="flex gap-2">
                          <input type="text" value={reading.operator} onChange={(e) => updateInspectionReading(reading.id, 'operator', e.target.value)} placeholder="Operator" className="flex-1 rounded pvs-input text-sm" />
                          <button type="button" onClick={() => removeInspectionReading(reading.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Witness Statements */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium text-pvs-text-muted">Witness Statements</label>
                  <button type="button" onClick={addWitnessStatement} className="flex items-center text-sm text-pvs-accent hover:text-pvs-accent/80"><Plus className="w-4 h-4 mr-1" /> Add Witness</button>
                </div>
                {formData.witnessStatements.length === 0 ? (
                  <p className="text-pvs-text-dim text-sm italic">No witness statements added yet.</p>
                ) : (
                  <div className="space-y-3">
                    {formData.witnessStatements.map((witness) => (
                      <div key={witness.id} className="p-3 bg-pvs-card-light rounded-lg">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                          <input type="text" value={witness.name} onChange={(e) => updateWitnessStatement(witness.id, 'name', e.target.value)} placeholder="Witness Name" className="rounded pvs-input text-sm" />
                          <input type="text" value={witness.role} onChange={(e) => updateWitnessStatement(witness.id, 'role', e.target.value)} placeholder="Role" className="rounded pvs-input text-sm" />
                          <input type="date" value={witness.date} onChange={(e) => updateWitnessStatement(witness.id, 'date', e.target.value)} className="rounded pvs-input text-sm" />
                          <button type="button" onClick={() => removeWitnessStatement(witness.id)} className="text-red-400 hover:text-red-300 justify-self-end"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <textarea value={witness.statement} onChange={(e) => updateWitnessStatement(witness.id, 'statement', e.target.value)} placeholder="Witness statement..." rows={2} className="w-full rounded pvs-input text-sm" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Section 6: Investigation & Actions */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4 flex items-center">
                <span className="w-8 h-8 rounded-full bg-pvs-accent text-pvs-bg flex items-center justify-center text-sm mr-3">6</span>
                Investigation & Actions
              </h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-pvs-text-muted mb-2">Initial Suspected Root Cause</label>
                    <select name="suspectedRootCause" value={formData.suspectedRootCause} onChange={handleInputChange} className="block w-full pvs-select">
                      <option value="">Select...</option>
                      <option value="Material Defect">Material Defect</option>
                      <option value="Manufacturing Fault">Manufacturing Fault</option>
                      <option value="Installation Error">Installation Error</option>
                      <option value="Operational Misuse">Operational Misuse</option>
                      <option value="Environmental Damage">Environmental Damage</option>
                      <option value="Age/Wear">Age/Wear</option>
                      <option value="Maintenance Failure">Maintenance Failure</option>
                      <option value="Unknown">Unknown - Requires Investigation</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Root Cause Details</label>
                  <textarea name="rootCauseDetails" value={formData.rootCauseDetails} onChange={handleInputChange} rows={2} placeholder="Provide detailed analysis of root cause..." className="block w-full pvs-input" />
                </div>

                <div className="p-4 bg-pvs-card-light rounded-lg">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-pvs-text-muted">Can the failure be rectified?</label>
                    <div className="flex items-center space-x-3">
                      <span className={`text-sm font-medium ${formData.canFailureBeRectified === 'no' ? 'text-red-400' : 'text-pvs-text-dim'}`}>No</span>
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, canFailureBeRectified: prev.canFailureBeRectified === 'yes' ? 'no' : 'yes', vesselToBeScrapped: prev.canFailureBeRectified === 'yes' ? prev.vesselToBeScrapped : 'no' }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.canFailureBeRectified === 'yes' ? 'bg-green-600' : 'bg-red-600'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.canFailureBeRectified === 'yes' ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                      <span className={`text-sm font-medium ${formData.canFailureBeRectified === 'yes' ? 'text-green-400' : 'text-pvs-text-dim'}`}>Yes</span>
                    </div>
                  </div>
                </div>

                {formData.canFailureBeRectified === 'no' && (
                  <div className="p-4 bg-red-900/30 rounded-lg border-2 border-red-500/30">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-red-400">Is this vessel to be scrapped?</label>
                      <div className="flex items-center space-x-3">
                        <span className={`text-sm font-medium ${formData.vesselToBeScrapped === 'no' ? 'text-pvs-text-dim' : 'text-pvs-text-dim'}`}>No</span>
                        <button type="button" onClick={() => setFormData(prev => ({ ...prev, vesselToBeScrapped: prev.vesselToBeScrapped === 'yes' ? 'no' : 'yes' }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.vesselToBeScrapped === 'yes' ? 'bg-red-600' : 'bg-gray-600'}`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.vesselToBeScrapped === 'yes' ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                        <span className={`text-sm font-medium ${formData.vesselToBeScrapped === 'yes' ? 'text-red-400' : 'text-pvs-text-dim'}`}>Yes</span>
                      </div>
                    </div>
                    {formData.vesselToBeScrapped === 'yes' && (
                      <p className="mt-2 text-sm text-red-400 font-medium">⚠️ This vessel has been marked for scrapping. Ensure all documentation is complete.</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Section 7: Close Out */}
            <div className="pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4 flex items-center">
                <span className="w-8 h-8 rounded-full bg-pvs-accent text-pvs-bg flex items-center justify-center text-sm mr-3">7</span>
                Close Out
              </h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-pvs-card-light rounded-lg">
                    <label className="text-sm font-medium text-pvs-text-muted">Corrective Action Verified</label>
                    <div className="flex items-center space-x-3">
                      <span className={`text-sm font-medium ${formData.correctiveActionVerified === 'no' ? 'text-red-400' : 'text-pvs-text-dim'}`}>No</span>
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, correctiveActionVerified: prev.correctiveActionVerified === 'yes' ? 'no' : 'yes' }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.correctiveActionVerified === 'yes' ? 'bg-green-600' : 'bg-red-600'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.correctiveActionVerified === 'yes' ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                      <span className={`text-sm font-medium ${formData.correctiveActionVerified === 'yes' ? 'text-green-400' : 'text-pvs-text-dim'}`}>Yes</span>
                    </div>
                  </div>
                  <div className="flex items-center p-3 bg-pvs-card-light rounded-lg">
                    <input type="checkbox" name="updatedInVesselInventory" checked={formData.updatedInVesselInventory} onChange={handleInputChange} className="h-4 w-4 text-pvs-accent focus:ring-pvs-accent/50 border-pvs-input-border rounded mr-3" />
                    <label className="text-sm font-medium text-pvs-text-muted">Updated in Vessel Inventory</label>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">QC Manager Sign-off <span className="text-xs text-pvs-text-dim">(Required for closure)</span></label><input type="text" name="qcManagerSignOff" value={formData.qcManagerSignOff} onChange={handleInputChange} placeholder="QC Manager name" className="block w-full pvs-input" /></div>
                  <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Sign-off Date</label><input type="date" name="qcManagerSignOffDate" value={formData.qcManagerSignOffDate} onChange={handleInputChange} className="block w-full pvs-input" /></div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-between pt-6 border-t border-pvs-border">
              <button type="button" onClick={onBack} className="flex items-center justify-center gap-2 px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50">
                <Home className="h-4 w-4" />
                Back to Forms
              </button>
              <button type="submit" disabled={isSubmitting} className={`flex items-center justify-center px-8 py-3 font-semibold rounded-lg shadow-lg transition-all ${isHighRisk ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-pvs-accent hover:bg-pvs-accent-dim text-pvs-bg'} ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {isSubmitting ? 'Submitting...' : (<><AlertCircle className="w-5 h-5 mr-2" />Log Vessel Failure</>)}
              </button>
            </div>
          </form>
        </div>
      </div>
      <SerialValidationModal isOpen={serialModalOpen} message={serialModalMessage} onClose={closeSerialModal} />
      <FormSuccessModal isOpen={showSuccessModal} formName="Vessel Failure Report" onClose={onBack} />
    </PVSPageLayout>
  );
};

export default VesselFailureReportForm;
